/**
 * A task's day — the sub-task.
 *
 * Reached from the task screen's Start / Continue. This is where the work is
 * actually recorded: instruction, why it works, and the day's report.
 *
 * Three states:
 *   not taken   → cannot report on a task that was never started; sends back
 *   open        → instruction + report form
 *   logged      → the day's result: points earned, the cycle so far, what's next
 *
 * Submitting does not navigate away. Marking the day flips this screen into its
 * result state, so the points land on the screen the user was already looking at
 * rather than on a different one they have to be sent to.
 *
 * Report fields are the four universal ones from the handoff. The per-exercise
 * field schema is a content field that does not exist yet, so rather than invent
 * one per exercise, these four are asked of every task: they are true of all of
 * them and none of them is a guess about the exercise's content.
 *
 * Input accent is constant `ACT`, never the pattern's zone colour — a form is an
 * action, not a measurement, and a hypothesis-zone task would otherwise render
 * as if it were disabled.
 */
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../i18n/LangContext';
import { formatDate } from '../i18n/detectLanguage';
import { useT, useTt } from '../i18n/useT';
import { AppShell, DeskRow } from './AppShell';
import { useAppContent, Exercise } from './content';
import { useProfile } from './profile';
import { Icon } from './icons';
import { Acc, Appear, Body, Btn, Col, Fluid, Meta, Muted, Panel, Rail, Row, useIsDesktop } from './primitives';
import { XpTag } from './XpTag';
import { splitPoints } from './points';
import { c, hair, hair2, f, ACT, effectiveStrength, DESKTOP, xpBase } from './tokens';
import { R } from './routes';
import { useTasks, doneCount, streakOf, isLoggedToday, todayEntry, dayKey, TaskEntry } from './tasks';

/**
 * Stored on the entry, so these are stable keys rather than sentences — a
 * report written in Ukrainian still reads correctly after switching to English.
 * Entries written before this change keep their literal text, which `t()`
 * passes through unchanged.
 */
const FRICTION = [
    'app.day.friction.time',
    'app.day.friction.forgot',
    'app.day.friction.chance',
    'app.day.friction.hard',
    'app.day.friction.mood',
    'app.day.friction.blocked',
];

/** Question label plus the required marker. */
const Q: React.FC<{ children: React.ReactNode; req?: boolean }> = ({ children, req }) => {
    const t = useT();

    return (
        <Row style={{ gap: 8, alignItems: 'flex-start', marginBottom: 13 }}>
        <span
            style={{
                fontSize: 15,
                color: c.textPri,
                fontWeight: 400,
                letterSpacing: '-0.015em',
                lineHeight: 1.35,
                flex: 1,
            }}
        >
            {children}
        </span>
            {req && (
                <Meta
                    style={{ fontSize: 8.5, color: c.subtle, letterSpacing: '0.12em', marginTop: 3, flex: '0 0 auto' }}
                >
                    {t('app.day.required')}
                </Meta>
            )}
        </Row>
    );
};

const FieldBox: React.FC<{ children: React.ReactNode; first?: boolean }> = ({ children, first }) => (
    <div style={{ padding: '18px 0', borderTop: first ? 'none' : hair }}>{children}</div>
);

const FBool: React.FC<{ value: boolean | null; onPick: (v: boolean) => void }> = ({ value, onPick }) => {
    const t = useT();

    return (
        <Row style={{ gap: 8 }}>
            {(
                [
                    [t('app.day.yes'), true],
                    [t('app.day.no'), false],
                ] as Array<[string, boolean]>
            ).map(([label, v]) => {
            const on = value === v;
            const col = v ? ACT : 'rgba(232,230,224,0.45)';

            return (
                <button
                    key={label}
                    type="button"
                    onClick={() => onPick(v)}
                    style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 9,
                        height: 52,
                        borderRadius: 14,
                        cursor: 'pointer',
                        border: on ? `1px solid ${col}` : hair,
                        background: on ? `${col}18` : c.card,
                        fontFamily: f.ui,
                        fontSize: 15,
                        fontWeight: 400,
                        color: on ? c.textPri : c.muted,
                    }}
                >
                    {on && <Icon k={v ? 'check' : 'close'} size={13} color={col} />}
                    {label}
                </button>
            );
        })}
        </Row>
    );
};

const FScale: React.FC<{ value: number | null; onPick: (v: number) => void; min?: number; max?: number }> = ({
    value,
    onPick,
    min = 0,
    max = 10,
}) => {
    const t = useT();
    const steps = Array.from({ length: max - min + 1 }, (_, i) => min + i);

    return (
        <div>
            <Row style={{ gap: 4 }}>
                {steps.map((v) => {
                    const on = v === value;
                    const filled = value != null && v <= value;
                    let h = 10;

                    if (on) h = 26;
                    else if (filled) h = 16;

                    return (
                        <Col key={v} style={{ flex: 1, alignItems: 'center' }} gap={7}>
                            <span style={{ fontFamily: f.mono, fontSize: 10, color: on ? ACT : 'transparent' }}>
                                {v}
                            </span>
                            <button
                                type="button"
                                aria-label={String(v)}
                                onClick={() => onPick(v)}
                                style={{
                                    width: '100%',
                                    height: h,
                                    borderRadius: 4,
                                    border: 'none',
                                    padding: 0,
                                    cursor: 'pointer',
                                    background: filled ? ACT : 'rgba(232,230,224,0.09)',
                                    boxShadow: on ? `0 0 10px ${ACT}66` : 'none',
                                    transition: 'height 160ms',
                                }}
                            />
                        </Col>
                    );
                })}
            </Row>
            <Row style={{ justifyContent: 'space-between', marginTop: 9 }}>
                <Meta style={{ fontSize: 9.5, color: c.subtle }}>{t('app.day.easy')}</Meta>
                <Meta style={{ fontSize: 9.5, color: c.subtle }}>{t('app.day.veryHard')}</Meta>
            </Row>
        </div>
    );
};

const FMulti: React.FC<{ opts: string[]; value: string[]; onToggle: (v: string) => void }> = ({
    opts,
    value,
    onToggle,
}) => {
    const t = useT();

    return (
        <div>
            <Row style={{ gap: 8, flexWrap: 'wrap' }}>
            {opts.map((o) => {
                const on = value.includes(o);

                return (
                    <button
                        key={o}
                        type="button"
                        onClick={() => onToggle(o)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '10px 13px',
                            borderRadius: 13,
                            cursor: 'pointer',
                            border: on ? `1px solid ${ACT}` : hair,
                            background: on ? `${ACT}18` : c.card,
                            fontFamily: f.ui,
                            fontSize: 13.5,
                            fontWeight: 300,
                            color: on ? c.textPri : c.text,
                        }}
                    >
                        <span
                            style={{
                                width: 16,
                                height: 16,
                                borderRadius: 4,
                                flex: '0 0 auto',
                                display: 'grid',
                                placeItems: 'center',
                                border: on ? 'none' : '1.5px solid rgba(232,230,224,0.22)',
                                background: on ? ACT : 'transparent',
                            }}
                        >
                            {on && <Icon k="check" size={10} color="#0a0a0a" />}
                        </span>
                        {t(o)}
                    </button>
                );
            })}
        </Row>
            <Meta style={{ fontSize: 9.5, color: c.subtle, display: 'block', marginTop: 11 }}>
                {t('app.day.multi', { n: value.length })}
            </Meta>
        </div>
    );
};

const TaskDayPage: React.FC = () => {
    const { code = '' } = useParams();
    const navigate = useNavigate();
    const { user, userProfile } = useAuth();
    const { lang } = useLang();
    const { t, tn } = useTt();
    const { content } = useAppContent();
    const profile = useProfile(content, user?.id, lang);
    const desktop = useIsDesktop(DESKTOP);
    const { list, mark, unmark } = useTasks();

    const ex = content?.exercises.find((e) => e.code === code);
    const task = list.find((t) => t.code === code);
    const pat = ex?.pats[0] ? content?.patBy[ex.pats[0]] : null;

    const [did, setDid] = React.useState<boolean | null>(null);
    const [hard, setHard] = React.useState<number | null>(null);
    const [friction, setFriction] = React.useState<string[]>([]);
    const [text, setText] = React.useState('');

    const toTask = React.useCallback(() => navigate(R.task(code)), [navigate, code]);

    if (!content || !ex) {
        return (
            <AppShell active="tasks" title={t('app.day.title')}>
                <Col style={{ padding: '60px 20px', alignItems: 'center' }} gap={10}>
                    <Meta style={{ color: c.subtle }}>
                        {t(content ? 'app.task.notFound' : 'app.state.loading')}
                    </Meta>
                    {content && <Btn onClick={() => navigate(R.tasks)}>{t('app.task.backToList')}</Btn>}
                </Col>
            </AppShell>
        );
    }

    const days = task?.days ?? 7;
    const doneN = task ? doneCount(task) : 0;
    const dayN = Math.min(doneN + 1, days);
    const logged = task ? isLoggedToday(task) : false;
    const entry = task ? todayEntry(task) : undefined;

    // The most recent day already recorded, today excluded — one line of continuity
    // between sessions. The whole archive stays on the task screen.
    const streak = task ? streakOf(task) : 0;
    const pastDays = task ? Object.entries(task.log).filter(([day, e]) => day !== dayKey() && e.done) : [];
    const previous = pastDays.sort((a, b) => (a[0] < b[0] ? 1 : -1))[0];

    const scoreOf = (dir: string) => profile.directions.find((x) => x.code === dir)?.score ?? null;

    const progress = (
        <Row style={{ gap: 4, marginTop: 16 }}>
            {Array.from({ length: days }, (_, i) => {
                let bg = 'rgba(232,230,224,0.1)';

                if (i < dayN - 1) bg = ACT;
                else if (i === dayN - 1) bg = c.textPri;

                return (
                    <div
                        key={`d${i}`}
                        style={{
                            flex: 1,
                            height: 3,
                            borderRadius: 2,
                            background: bg,
                            boxShadow: i === dayN - 1 ? `0 0 8px ${c.textPri}66` : 'none',
                        }}
                    />
                );
            })}
        </Row>
    );

    const navHeader = (
        <Row style={{ padding: '14px 16px 12px', gap: 12, borderBottom: hair }}>
            <button
                type="button"
                aria-label={t('app.action.back')}
                onClick={toTask}
                style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    border: hair,
                    background: c.card,
                    display: 'grid',
                    placeItems: 'center',
                    flex: '0 0 auto',
                    cursor: 'pointer',
                }}
            >
                <Icon k="back" size={14} color={c.muted} />
            </button>
            <Col style={{ flex: 1, alignItems: 'center' }} gap={3}>
                <Meta style={{ color: c.textPri, letterSpacing: '0.18em' }}>
                    {t('app.task.dayOf', { day: dayN, days })}
                </Meta>
                <Meta style={{ fontSize: 9.5, color: c.subtle, letterSpacing: '0.12em' }}>{t('app.tasks.daily')}</Meta>
            </Col>
            <div style={{ width: 32, flex: '0 0 auto' }} />
        </Row>
    );

    const shellProps = {
        active: 'tasks' as const,
        title: ex.name,
        subtitle: t('app.day.subtitle', { day: dayN, days }),
        index: profile.index,
        email: userProfile?.email || user?.email,
        tasksTotal: list.length,
        mobileHeader: !desktop ? navHeader : undefined,
        // Cycle progress and what the day is worth, in the bar — the body below
        // is then only the work itself.
        chips: (
            <Row style={{ gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ width: 150 }}>{progress}</div>
                <XpTag ex={ex} content={content} scoreOf={scoreOf} />
                {pat && (
                    <>
                        <Meta style={{ fontSize: 10, color: c.subtle }}>·</Meta>
                        <Meta style={{ fontSize: 10 }}>{pat.name}</Meta>
                    </>
                )}
            </Row>
        ),
    };

    /* ── not taken yet ───────────────────────────────── */
    if (!task) {
        return (
            <AppShell {...shellProps}>
                <Col
                    style={{
                        padding: '60px 24px',
                        alignItems: 'center',
                        textAlign: 'center',
                        maxWidth: 460,
                        margin: '0 auto',
                    }}
                    gap={16}
                >
                    <div
                        style={{
                            fontFamily: f.serif,
                            fontSize: 22,
                            fontWeight: 300,
                            color: c.textPri,
                            lineHeight: 1.25,
                        }}
                    >
                        {t('app.day.notTaken.title')}
                    </div>
                    <Muted>{t('app.day.notTaken.body')}</Muted>
                    <div style={{ width: '100%', maxWidth: 300, marginTop: 6 }}>
                        <Btn primary ic="arrow" onClick={toTask}>
                            {t('app.tasks.open')}
                        </Btn>
                    </div>
                </Col>
            </AppShell>
        );
    }

    /* ── today's work already done ───────────────────── */
    if (logged) {
        const perDay = xpBase(ex.min);
        const collected = doneN * perDay;
        const skipped = task ? Object.values(task.log).filter((e) => !e.done).length : 0;
        const byDay = task ? Object.entries(task.log).sort((a, b) => (a[0] < b[0] ? -1 : 1)) : [];
        const cycleDone = doneN >= days;

        // Whatever is the strongest blocker now and is not already in work. No
        // scheduler exists, so the screen promises "tomorrow", not a clock time.
        const ranked = profile.patterns
            .filter((p) => p.pct != null)
            .sort((a, b) => effectiveStrength(b.pct as number, b.pol) - effectiveStrength(a.pct as number, a.pol));
        const nextEx = ranked.reduce<Exercise | undefined>(
            (found, p) =>
                found ||
                (content.exsByPattern[p.code] || [])
                    .slice()
                    .sort((a, b) => (a.min ?? 5) - (b.min ?? 5))
                    .find((e) => e.code !== ex.code && !list.some((t) => t.code === e.code)),
            undefined,
        );

        const resultHero = (
            <div
                style={{
                    padding: '22px 24px 24px',
                    borderRadius: 18,
                    border: `1px solid ${ACT}33`,
                    background: `linear-gradient(140deg, ${ACT}0f, ${c.card} 62%)`,
                }}
            >
                <Row style={{ gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <div
                        style={{
                            width: 62,
                            height: 62,
                            borderRadius: '50%',
                            display: 'grid',
                            placeItems: 'center',
                            flex: '0 0 auto',
                            border: `1px solid ${entry?.done ? ACT : hair}`,
                            boxShadow: entry?.done ? `0 0 22px ${ACT}44` : 'none',
                        }}
                    >
                        <Icon k={entry?.done ? 'check' : 'close'} size={24} color={entry?.done ? ACT : c.subtle} />
                    </div>
                    <Col style={{ flex: 1, minWidth: 220 }} gap={9}>
                        <Meta style={{ fontSize: 9.5, letterSpacing: '0.16em', color: ACT }}>
                            {t(entry?.done ? 'app.day.result.done' : 'app.day.result.skipped')}
                        </Meta>
                        <div
                            style={{
                                fontFamily: f.serif,
                                fontSize: desktop ? 30 : 24,
                                fontWeight: 300,
                                color: c.textPri,
                                lineHeight: 1.15,
                            }}
                        >
                            {ex.name}
                        </div>
                        <Muted style={{ fontSize: 13.5 }}>
                            {cycleDone
                                ? t('app.day.cycleDone')
                                : t('app.day.dayLogged', { day: doneN, days })}
                        </Muted>
                        <Row style={{ gap: 9, flexWrap: 'wrap', marginTop: 2 }}>
                            {nextEx && (
                                <Row
                                    style={{
                                        gap: 7,
                                        padding: '5px 12px',
                                        borderRadius: 99,
                                        border: hair,
                                    }}
                                >
                                    <Icon k="clock" size={12} color={c.muted} />
                                    <Meta style={{ fontSize: 9.5 }}>{t('app.day.nextTomorrow')}</Meta>
                                </Row>
                            )}
                            {streak > 0 && (
                                <Row
                                    style={{
                                        gap: 7,
                                        padding: '5px 12px',
                                        borderRadius: 99,
                                        border: `1px solid ${ACT}44`,
                                    }}
                                >
                                    <Icon k="flame" size={12} color={ACT} />
                                    <Meta style={{ fontSize: 9.5, color: ACT }}>
                                        {tn('app.day.streakHolds', streak)}
                                    </Meta>
                                </Row>
                            )}
                        </Row>
                    </Col>
                </Row>
                <Row style={{ gap: 16, marginTop: 20, paddingTop: 18, borderTop: hair2, flexWrap: 'wrap' }}>
                    {(
                        [
                            [String(collected), t('app.day.stat.points'), ACT],
                            [String(days), t('app.day.stat.days'), c.textPri],
                            [String(doneN), t('app.task.entries'), c.textPri],
                            [String(skipped), t('app.day.stat.skipped'), c.textPri],
                        ] as Array<[string, string, string]>
                    ).map(([v, l, col]) => (
                        <Col key={l} style={{ flex: '1 1 90px', minWidth: 0 }} gap={5}>
                            <span style={{ fontFamily: f.mono, fontSize: 26, fontWeight: 300, color: col }}>{v}</span>
                            <Meta style={{ fontSize: 9, letterSpacing: '0.12em' }}>{l}</Meta>
                        </Col>
                    ))}
                </Row>
            </div>
        );

        const whatsNext = (
            <div>
                <Meta style={{ fontSize: 9.5, letterSpacing: '0.16em', display: 'block', marginBottom: 15 }}>
                    {t('app.day.whatsNext')}
                </Meta>
                <Row style={{ gap: 13, alignItems: 'flex-start' }}>
                    <div
                        style={{
                            width: 34,
                            height: 34,
                            borderRadius: 11,
                            border: hair,
                            display: 'grid',
                            placeItems: 'center',
                            flex: '0 0 auto',
                        }}
                    >
                        <Icon k="clock" size={15} color={ACT} />
                    </div>
                    <Col style={{ flex: 1, minWidth: 0 }} gap={6}>
                        <div style={{ fontSize: 15.5, color: c.textPri, letterSpacing: '-0.015em' }}>
                            {cycleDone
                                ? t('app.day.next.newExercise')
                                : t('app.day.next.day', { day: doneN + 1, days })}
                        </div>
                        <Muted style={{ fontSize: 13 }}>
                            {t(cycleDone ? 'app.day.next.newExercise.body' : 'app.day.next.day.body')}
                        </Muted>
                    </Col>
                </Row>
            </div>
        );

        const pointsByDay = (
            <div>
                <Row style={{ justifyContent: 'space-between', marginBottom: 16 }}>
                    <Meta style={{ fontSize: 9.5, letterSpacing: '0.16em' }}>{t('app.day.pointsByDay')}</Meta>
                    <Meta style={{ fontSize: 9.5, color: c.subtle }}>
                        {t('app.day.ofDays', { n: doneN, days })}
                    </Meta>
                </Row>
                <Row style={{ gap: 7, alignItems: 'flex-end' }}>
                    {Array.from({ length: days }, (_, i) => {
                        const rec = byDay[i];
                        const scored = rec && rec[1].done;

                        return (
                            <Col key={`bar${i}`} style={{ flex: 1, alignItems: 'center' }} gap={7}>
                                <span style={{ fontFamily: f.mono, fontSize: 10, color: scored ? c.muted : c.subtle }}>
                                    {scored ? perDay : '–'}
                                </span>
                                <div
                                    style={{
                                        width: '100%',
                                        height: scored ? 46 : 8,
                                        borderRadius: 8,
                                        background: scored ? ACT : 'transparent',
                                        border: scored ? 'none' : `1px dashed ${c.subtle}55`,
                                        opacity: scored ? 1 : 0.6,
                                    }}
                                />
                                <span style={{ fontFamily: f.mono, fontSize: 9, color: c.subtle }}>{i + 1}</span>
                            </Col>
                        );
                    })}
                </Row>
            </div>
        );

        // Both halves of the split, because they answer different questions:
        // the directions are where the number on the dashboard moved, and the
        // patterns are what the work was actually aimed at. An exercise can name
        // three, and showing only its track hid two of them.
        const split = splitPoints(ex, content);

        const wherePoints = (
            <div>
                <Meta style={{ fontSize: 9.5, letterSpacing: '0.16em', display: 'block', marginBottom: 14 }}>
                    {t('app.day.wherePoints')}
                </Meta>
                <Col gap={9}>
                    {split.byDirection.map(([dir, pts]) => (
                        <Row
                            key={dir}
                            style={{
                                gap: 11,
                                padding: '12px 14px',
                                borderRadius: 13,
                                border: hair,
                                background: c.cardIn,
                            }}
                        >
                            <Icon k={dir} size={14} color={ACT} />
                            <div style={{ fontSize: 14, color: c.textPri, flex: 1, minWidth: 0 }}>
                                {content.dirBy[dir]?.disp}
                            </div>
                            <span style={{ fontFamily: f.mono, fontSize: 13, color: ACT }}>+{pts * doneN}</span>
                        </Row>
                    ))}
                </Col>

                {split.byPattern.length > 0 && (
                    <>
                        <Meta
                            style={{
                                fontSize: 9.5,
                                letterSpacing: '0.16em',
                                display: 'block',
                                margin: '20px 0 12px',
                            }}
                        >
                            {t('app.day.whichPatterns')}
                        </Meta>
                        <Col gap={0}>
                            {split.byPattern.map(([code, pts], i) => {
                                const p = content.patBy[code];

                                return (
                                    <Row
                                        key={code}
                                        style={{ gap: 11, padding: '11px 0', borderTop: i ? hair2 : 'none' }}
                                    >
                                        <span
                                            style={{
                                                width: 5,
                                                height: 5,
                                                borderRadius: '50%',
                                                background: ACT,
                                                flex: '0 0 auto',
                                            }}
                                        />
                                        <div style={{ fontSize: 13.5, color: c.text, flex: 1, minWidth: 0 }}>
                                            {p?.name || code}
                                        </div>
                                        <span style={{ fontFamily: f.mono, fontSize: 12.5, color: ACT }}>
                                            +{pts * doneN}
                                        </span>
                                    </Row>
                                );
                            })}
                        </Col>
                        {split.byPattern.length > 1 && (
                            <Muted style={{ fontSize: 11.5, marginTop: 11 }}>
                                {t('app.day.splitNote', { n: split.byPattern.length })}
                            </Muted>
                        )}
                    </>
                )}
            </div>
        );

        const resultActions = (
            <Row style={{ gap: 12, flexWrap: 'wrap' }}>
                {/* Back to the list, not to this task: the day is done, so the
                    next thing the user wants is everything else. */}
                <div style={{ flex: '1 1 200px' }}>
                    <Btn ic="arrow" onClick={() => navigate(R.tasks)}>
                        {t('app.day.toTasks')}
                    </Btn>
                </div>
                <button
                    type="button"
                    onClick={() => unmark(ex.code)}
                    style={{ background: 'none', border: 'none', padding: 6, cursor: 'pointer' }}
                >
                    <Meta style={{ fontSize: 9.5, color: c.subtle, letterSpacing: '0.1em' }}>
                        {t('app.day.redo')}
                    </Meta>
                </button>
            </Row>
        );

        const resultShell = {
            ...shellProps,
            subtitle: t(entry?.done ? 'app.day.result.done' : 'app.day.result.skippedShort'),
            chips: (
                <Row
                    style={{
                        gap: 8,
                        padding: '6px 13px',
                        borderRadius: 99,
                        border: `1px solid ${ACT}55`,
                    }}
                >
                    <Icon k="check" size={12} color={ACT} />
                    <Meta style={{ fontSize: 10, color: ACT }}>
                        {tn('app.day.daysCount', doneN)} · {tn('app.state.points', collected)}
                    </Meta>
                </Row>
            ),
        };

        if (desktop) {
            return (
                <AppShell {...resultShell}>
                    <Appear>
                        <DeskRow>
                            <Fluid>
                                {resultHero}
                                <Panel pad="18px 24px 22px">{whatsNext}</Panel>
                                <Panel pad="18px 24px 20px">{resultActions}</Panel>
                            </Fluid>
                            <Rail width={380}>
                                <Panel pad="18px 20px 20px">{pointsByDay}</Panel>
                                <Panel pad="18px 20px 20px">{wherePoints}</Panel>
                            </Rail>
                        </DeskRow>
                    </Appear>
                </AppShell>
            );
        }

        return (
            <AppShell {...resultShell}>
                <Appear>
                    <Col style={{ padding: '18px 20px 30px' }} gap={18}>
                        {resultHero}
                        {pointsByDay}
                        {wherePoints}
                        {whatsNext}
                        {resultActions}
                    </Col>
                </Appear>
            </AppShell>
        );
    }

    /* ── the day is open ─────────────────────────────── */
    const canSubmit = did !== null;

    const submit = () => {
        if (!canSubmit) return;
        const rec: TaskEntry = { done: did as boolean };

        if (did && hard != null) rec.hard = hard;
        if (!did && friction.length) rec.friction = friction;
        if (text.trim()) rec.text = text.trim();
        mark(ex.code, rec);
        // Stay here: marking flips this screen into its result state, which is
        // where the day's points and what comes next are shown.
    };

    // What today's work is. A per-day title is not authored yet, so the card names
    // the exercise itself rather than inventing a heading for each of the 7 days.
    const dayCard = (
        <div
            style={{
                padding: '17px 19px 19px',
                borderRadius: 18,
                border: `1px solid ${ACT}33`,
                background: `linear-gradient(140deg, ${ACT}0d, ${c.card} 60%)`,
            }}
        >
            <Row style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <Meta style={{ fontSize: 9.5, letterSpacing: '0.16em', color: ACT }}>{t('app.day.dayTask')}</Meta>
                <Meta style={{ fontSize: 9.5, color: c.subtle }}>
                    {tn('app.task.stepCount', ex.flow.length)}
                    {ex.min ? ` · ${t('app.task.min', { n: ex.min })}` : ''}
                </Meta>
            </Row>
            <div
                style={{
                    fontSize: desktop ? 24 : 20,
                    color: c.textPri,
                    fontWeight: 400,
                    letterSpacing: '-0.025em',
                    lineHeight: 1.2,
                    marginTop: 11,
                }}
            >
                {ex.name}
            </div>
        </div>
    );

    const lastTime = previous && (
        <div>
            <Row style={{ justifyContent: 'space-between', marginBottom: 11 }}>
                <Meta style={{ fontSize: 9.5, letterSpacing: '0.16em' }}>{t('app.day.lastTime')}</Meta>
                <Meta style={{ fontSize: 9.5, color: c.subtle }}>
                    {formatDate(lang, new Date(previous[0]), {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                    }).toUpperCase()}
                </Meta>
            </Row>
            <Body style={{ fontSize: 13.5 }}>{previous[1].text || t('app.day.doneNoNote')}</Body>
            <Muted style={{ fontSize: 11.5, marginTop: 11 }}>{t('app.day.fullHistory')}</Muted>
        </div>
    );

    const instruction = (
        <>
            {/* The authored steps for this exercise, in order. */}
            <div
                style={{
                    padding: '16px 17px',
                    border: hair,
                    borderRadius: 16,
                    background: c.card,
                }}
            >
                <Row style={{ justifyContent: 'space-between', marginBottom: 13 }}>
                    <Meta style={{ fontSize: 9.5, letterSpacing: '0.16em' }}>{t('app.day.instruction')}</Meta>
                    <Meta style={{ fontSize: 9.5, color: c.subtle }}>
                        {ex.min ? t('app.task.min', { n: ex.min }) : t('app.task.rule')}
                    </Meta>
                </Row>
                <Col gap={12}>
                    {ex.flow.map((step, i) => (
                        <Row key={step.slice(0, 24)} style={{ gap: 12, alignItems: 'flex-start' }}>
                            <span
                                style={{
                                    fontFamily: f.mono,
                                    fontSize: 11,
                                    color: ACT,
                                    flex: '0 0 auto',
                                    width: 18,
                                    marginTop: 2,
                                }}
                            >
                                {String(i + 1).padStart(2, '0')}
                            </span>
                            <Body style={{ fontSize: 13.5 }}>{step}</Body>
                        </Row>
                    ))}
                </Col>
                {task.note && (
                    <div style={{ marginTop: 14, paddingLeft: 13, borderLeft: `2px solid ${ACT}55` }}>
                        <Meta style={{ fontSize: 9, letterSpacing: '0.12em', color: c.subtle }}>
                            {t('app.day.yourNote')}
                        </Meta>
                        <Muted style={{ fontSize: 13, marginTop: 6 }}>{task.note}</Muted>
                    </div>
                )}
            </div>

            {/* The exercise's own mechanism leads; the pattern's mechanism
                is the wider context behind it. Open on desktop, where there is
                room to read it beside the form rather than instead of it. */}
            <div style={{ marginTop: 20 }}>
                <Acc title={t('app.dir.why')} meta={pat?.name} hex={ACT} open={desktop} last>
                    <Col gap={13}>
                        <Body>{ex.why}</Body>
                        {(pat?.mech || []).slice(0, 1).map((para) => (
                            <Body key={para.slice(0, 24)}>{para}</Body>
                        ))}
                    </Col>
                </Acc>
            </div>
        </>
    );

    const report = (
        <div>
            <Row style={{ justifyContent: 'space-between', marginBottom: 4 }}>
                <Meta style={{ fontSize: 9.5, letterSpacing: '0.16em' }}>{t('app.day.report')}</Meta>
                <Meta style={{ fontSize: 10, color: c.subtle }}>{tn('app.day.fields', 3)}</Meta>
            </Row>

            <FieldBox first>
                <Q req>{t('app.day.q.did')}</Q>
                <FBool value={did} onPick={setDid} />
            </FieldBox>

            {did === true && (
                <FieldBox>
                    <Q>{t('app.day.q.hard')}</Q>
                    <FScale value={hard} onPick={setHard} />
                </FieldBox>
            )}

            {did === false && (
                <FieldBox>
                    <Q>{t('app.day.q.friction')}</Q>
                    <FMulti
                        opts={FRICTION}
                        value={friction}
                        onToggle={(v) =>
                            setFriction((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]))
                        }
                    />
                </FieldBox>
            )}

            <FieldBox>
                <Q>{t('app.day.q.noticed')}</Q>
                <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={t('app.day.q.noticed.placeholder')}
                    rows={3}
                    style={{
                        width: '100%',
                        resize: 'vertical',
                        background: c.card,
                        border: `1px solid ${text ? `${ACT}55` : 'rgba(232,230,224,0.10)'}`,
                        borderRadius: 14,
                        padding: '14px 15px',
                        color: c.textPri,
                        fontFamily: f.ui,
                        fontSize: 14,
                        lineHeight: 1.55,
                        outline: 'none',
                    }}
                />
            </FieldBox>

            <div style={{ marginTop: 18, paddingTop: 18, borderTop: hair }}>
                <Btn primary={canSubmit} ic="check" onClick={submit} style={canSubmit ? undefined : { opacity: 0.45 }}>
                    {t('app.day.finish')}
                </Btn>
                <Row style={{ justifyContent: 'center', marginTop: 12 }}>
                    <Meta style={{ fontSize: 9.5, color: c.subtle }}>
                        {t(canSubmit ? 'app.day.willBeSaved' : 'app.day.needAnswer')}
                    </Meta>
                </Row>
            </div>
        </div>
    );

    if (desktop) {
        return (
            <AppShell {...shellProps}>
                <Appear>
                    <DeskRow>
                        <Fluid>
                            {dayCard}
                            {instruction}
                            {lastTime && <Panel pad="18px 22px 20px">{lastTime}</Panel>}
                        </Fluid>
                        {/* The form is the point of this screen, so it gets its own
                            column and stays in view while the instruction is read. */}
                        <Rail width={420}>
                            <Panel pad="18px 22px 22px">{report}</Panel>
                        </Rail>
                    </DeskRow>
                </Appear>
            </AppShell>
        );
    }

    return (
        <AppShell {...shellProps}>
            <Appear>
                <Col style={{ padding: '18px 20px 30px' }} gap={20}>
                    {progress}
                    {dayCard}
                    {instruction}
                    {report}
                    {lastTime}
                </Col>
            </Appear>
        </AppShell>
    );
};

export default TaskDayPage;
